<script setup lang="ts">
import type { Child } from 'src/modules/Child/models/Child';
import {
  mdiCheck,
  mdiClose,
  mdiPencil,
  mdiDelete,
  mdiKey,
  mdiCalendar,
} from '@quasar/extras/mdi-v7';
import BCard from 'src/shared/components/base/BCard/BCard.vue';
import { date } from 'quasar';
interface Props {
  processingLicense: boolean;
}
defineProps<Props>();
const child = defineModel<Partial<Child>>('child');

const emits = defineEmits<{
  editChild: [];
  deleteChild: [];
  toggleActive: [];
  loginToken: [];
  checkout: [];
}>();
</script>

<template>
  <BCard
    size="sm"
    class="full-width"
  >
    <q-card-section>
      <q-item>
        <q-item-section>
          <template v-if="child?.validLicense">
            <q-item-label class="text-positive text-caption justify-start">
              {{ $t('LICENSE_VALID_UNTIL') }}
            </q-item-label>
            <q-item-label class="text-left">
              <q-spinner
                v-if="processingLicense"
                color="primary"
                size="3em"
              />
              <q-chip
                v-else
                :date="child?.mostRecentLicense?.valid_until"
                dense
                :icon="mdiCalendar"
                class="text-caption text-muted q-ma-none"
                color="blue-grey-9"
              >
                {{
                  date.formatDate(
                    child?.mostRecentLicense?.valid_until,
                    'DD-MM-YYYY',
                  )
                }}
              </q-chip>
            </q-item-label>
          </template>
          <q-item-label
            v-if="!child?.mostRecentLicense?.valid_until"
            class="text-negative text-caption"
          >
            {{ $t('NO_LICENSE') }}
          </q-item-label>
          <template
            v-if="!child?.validLicense && child?.mostRecentLicense?.valid_until"
          >
            <q-item-label class="text-negative text-caption">
              {{ $t('LICENSE_EXPIRED') }}
            </q-item-label>
            <q-item-label class="text-left">
              <q-chip
                :date="child?.mostRecentLicense?.valid_until"
                dense
                :icon="mdiCalendar"
                class="text-caption text-negative q-ma-none"
                color="blue-grey-9"
              >
                {{
                  date.formatDate(
                    child?.mostRecentLicense?.valid_until,
                    'DD-MM-YYYY',
                  )
                }}
              </q-chip>
            </q-item-label>
          </template>
        </q-item-section>

        <q-item-section
          v-if="!child?.validLicense"
          side
        >
          <q-btn
            :label="$t('BUY_LICENSE')"
            color="blue-grey-9"
            size="sm"
            @click="emits('checkout')"
          />
        </q-item-section>
      </q-item>
      <q-separator />

      <q-item>
        <q-item-section
          v-if="
            child &&
            'is_active' in child &&
            child?.mostRecentLicense?.documentId
          "
          top
        >
          <q-toggle
            :class="{
              'text-accent': child.is_active,
              'text-muted': !child.is_active,
            }"
            :model-value="child.is_active"
            :label="child.is_active ? $t('ACTIVE') : $t('INACTIVE')"
            toggle-color="grey-5"
            :checked-icon="mdiCheck"
            :unchecked-icon="mdiClose"
            @update:model-value="emits('toggleActive')"
          />
        </q-item-section>
        <q-space></q-space>
        <q-item-section side>
          <q-btn
            color="dark"
            dense
            round
            :icon="mdiPencil"
            @click="emits('editChild')"
          />
        </q-item-section>
        <q-item-section
          v-if="!child?.validLicense"
          side
        >
          <q-btn
            dense
            round
            color="negative"
            :icon="mdiDelete"
            @click="emits('deleteChild')"
          />
        </q-item-section>
      </q-item>
      <q-item>
        <q-item-section>
          <div
            class="text-accent ellipsis"
            :class="{
              'text-h6': $q.platform.is.desktop,
              'text-subtitle1': !$q.platform.is.desktop,
            }"
          >
            {{ child?.firstname }} {{ child?.lastname }}
          </div>
          <q-item-label class="text-subtitle2 text-accent q-mt-sm">
            <q-chip
              square
              dense
              outline
              class="chip-width"
              color="blue-grey-9"
              >{{ $t('SCHOOL') }}</q-chip
            >
            {{
              child?.public_school?.short_name ||
              child?.public_school?.full_name ||
              child?.school
            }}
          </q-item-label>
          <q-item-label
            v-if="child?.public_school?.city?.city"
            class="text-subtitle2 text-accent"
          >
            <q-chip
              square
              dense
              outline
              class="chip-width"
              color="blue-grey-9"
              >{{ $t('CITY') }}</q-chip
            >
            {{ child?.public_school?.city?.city }}
          </q-item-label>
          <q-item-label class="text-subtitle2 text-accent">
            <q-chip
              outline
              square
              dense
              class="chip-width"
              color="blue-grey-9"
              >{{ $t('CLASS') }}</q-chip
            >
            {{ child?.class }}
          </q-item-label>
        </q-item-section>
      </q-item>
    </q-card-section>

    <q-separator dark />

    <q-card-section>
      <q-card-actions align="right">
        <q-btn
          outline
          :icon="mdiKey"
          :label="$t('LOGIN_TOKEN')"
          @click="emits('loginToken')"
        />
      </q-card-actions>
      <p class="text-caption text-border text-right q-mt-xs q-px-sm">
        {{ $t('GENERATE_ONE_TIME_TOKEN_FOR_CHILD') }}
      </p>
    </q-card-section>
  </BCard>
</template>
<style lang="scss" scoped>
.chip-width {
  width: 6em;
  > * {
    justify-content: center;
  }
}
</style>
